'use client';

import { c as _c } from "react/compiler-runtime";
import { createContext, FC, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { Button } from '@ordering/components/ui/Button';
import { SessionStorageName } from '@ordering/types/session';
import { useStorefrontConfig } from '@ordering/providers/StorefrontConfig/StorefrontConfig';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { useSchedule } from '@ordering/providers/ScheduleProvider/ScheduleProvider';
import { useAllowPreorderWhenClosed } from '@ordering/hooks/useShouldSelectPreorderTime/useShouldSelectPreorderTime';
import { useGetFulfilAt } from '@ordering/stores/OrderStore/hooks';
import { useFeatures } from '@ordering/providers/FeaturesProvider';
const AcceptingOrdersContext = createContext<{
  isAcceptingOrders: boolean;
  isBrowsingMenu: boolean;
  isCurrentlyOpen: boolean;
  isValidFuturePreorder: boolean;
  setBrowsingMenu: (browsingMenu: boolean) => void;
}>({
  isAcceptingOrders: true,
  isBrowsingMenu: false,
  isCurrentlyOpen: true,
  isValidFuturePreorder: false,
  setBrowsingMenu: () => {}
});
export const AcceptingOrdersProvider: FC<{
  children: ReactNode;
  browsingMenu?: boolean;
}> = t0 => {
  const $ = _c(24);
  const {
    children,
    browsingMenu: t1
  } = t0;
  const browsingMenu = t1 === undefined ? true : t1;
  const t = useTranslations();
  const router = useRouter();
  const {
    isCurrentlyOpen
  } = useSchedule();
  const {
    details
  } = useStorefrontConfig();
  const {
    isDigitalMenuStore
  } = useFeatures();
  const merchantName = details.storeName;
  const [isBrowsingMenu, setBrowsingMenu] = useState(browsingMenu);
  const allowPreorderWhenClosed = useAllowPreorderWhenClosed();
  const kitchenIsOpen = details.storeStatus === "OPEN";
  const storefrontIsOpen = !details.isPaused;
  const acceptingOrders = isDigitalMenuStore || (isCurrentlyOpen || allowPreorderWhenClosed) && kitchenIsOpen && storefrontIsOpen;
  const fufilAt = useGetFulfilAt();
  const hasValidPreorderTime = fufilAt != null;
  const isValidFuturePreorder = isCurrentlyOpen || hasValidPreorderTime && allowPreorderWhenClosed;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setBrowsingMenu(true);
      sessionStorage.setItem(SessionStorageName.BROWSING_MENU, "true");
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const onBrowseMenuClick = t2;
  let t3;
  if ($[1] !== acceptingOrders) {
    t3 = () => {
      if (acceptingOrders) {
        sessionStorage.removeItem(SessionStorageName.BROWSING_MENU);
      } else {
        if (sessionStorage.getItem(SessionStorageName.BROWSING_MENU) != "true") {
          setBrowsingMenu(false);
        }
      }
    };
    $[1] = acceptingOrders;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== acceptingOrders || $[4] !== isDigitalMenuStore) {
    t4 = [isDigitalMenuStore, acceptingOrders];
    $[3] = acceptingOrders;
    $[4] = isDigitalMenuStore;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  useEffect(t3, t4);
  let t5;
  if ($[6] !== acceptingOrders || $[7] !== isBrowsingMenu || $[8] !== isCurrentlyOpen || $[9] !== isValidFuturePreorder) {
    t5 = {
      isAcceptingOrders: acceptingOrders,
      isBrowsingMenu,
      isCurrentlyOpen,
      isValidFuturePreorder,
      setBrowsingMenu
    };
    $[6] = acceptingOrders;
    $[7] = isBrowsingMenu;
    $[8] = isCurrentlyOpen;
    $[9] = isValidFuturePreorder;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const acceptingOrdersMemo = t5;
  let t6;
  if ($[11] !== acceptingOrders || $[12] !== isBrowsingMenu || $[13] !== merchantName || $[14] !== router || $[15] !== storefrontIsOpen || $[16] !== t) {
    t6 = !acceptingOrders && !isBrowsingMenu && <div className="fixed w-full h-full bg-black/40 z-50 flex flex-col items-center justify-center"><section className="bg-white p-6 shadow-md rounded-lg max-w-[280px]"><h1 className="font-bold">{t("global.not_accepting_orders.title")}</h1><p className="mt-2 text-[14px]">{storefrontIsOpen ? t("global.not_accepting_orders.description", {
            merchantName
          }) : t("global.not_accepting_orders.description_closed")}</p><div className="flex col mt-2 gap-2"><Button variant="black" onClick={onBrowseMenuClick}>{t("global.not_accepting_orders.browse_button")}</Button><Button variant="outline" onClick={() => router.refresh()}>{t("global.not_accepting_orders.retry_button")}</Button></div></section></div>;
    $[11] = acceptingOrders;
    $[12] = isBrowsingMenu;
    $[13] = merchantName;
    $[14] = router;
    $[15] = storefrontIsOpen;
    $[16] = t;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== acceptingOrdersMemo || $[19] !== children) {
    t7 = <AcceptingOrdersContext.Provider value={acceptingOrdersMemo}>{children}</AcceptingOrdersContext.Provider>;
    $[18] = acceptingOrdersMemo;
    $[19] = children;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== t6 || $[22] !== t7) {
    t8 = <>{t6}{t7}</>;
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  return t8;
};
export const useAcceptingOrders = () => {
  return useContext(AcceptingOrdersContext);
};